import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import AvatarBrown from '../../assets/images/avatar_brown.svg';
import BronzeMedal from '../../assets/images/icons/bronze.svg';
import GoldMedal from '../../assets/images/icons/gold.svg';
import SilverMedal from '../../assets/images/icons/silver.svg';

export interface PodiumUser {
  id: string;
  name: string;
  points: number;
  medal: 'gold' | 'silver' | 'bronze';
}

interface RankingPodiumProps {
  users: [PodiumUser, PodiumUser, PodiumUser]; 
}

export default function RankingPodium({ users }: RankingPodiumProps) {
  const { t } = useTranslation();
  const [gold, silver, bronze] = users.sort((a, b) => {
    const order = { gold: 1, silver: 2, bronze: 3 };
    return order[a.medal] - order[b.medal];
  });

  const getMedalGradient = (medal: 'gold' | 'silver' | 'bronze'): [string, string, string] => {
    switch (medal) {
      case 'gold':
        return ['#FFEE00', '#F2C200', '#FFA600'];
      case 'silver':
        return ['#F2F2F2', '#D1D1D1', '#979797'];
      case 'bronze':
        return ['#E5D289', '#D8931C', '#AB5F18'];
    }
  };

  const getMedalIcon = (medal: 'gold' | 'silver' | 'bronze') => {
    switch (medal) {
      case 'gold':
        return <GoldMedal width={24} height={24} />;
      case 'silver':
        return <SilverMedal width={24} height={24} />;
      case 'bronze':
        return <BronzeMedal width={24} height={24} />;
    }
  };

  const PodiumItem = ({ user, position }: { user: PodiumUser; position: 1 | 2 | 3 }) => {
    const height = position === 1 ? 164 : position === 2 ? 116 : 100;
    
    return (
      <View style={styles.podiumItemContainer}>
        {/* Avatar with medal */}
        <View style={styles.avatarContainer}>
          <AvatarBrown width={48} height={48} />
          <View style={styles.medalBadge}>
            {getMedalIcon(user.medal)}
          </View>
        </View>
        
        {/* Name */}
        <Text style={styles.userName} numberOfLines={1}>
          {user.name}
        </Text>
        
        {/* Points */}
        <Text style={styles.userPoints}>{t('common.pointsFull', { points: user.points })}</Text>
        
        {/* Podium block */}
        <LinearGradient
          colors={getMedalGradient(user.medal)}
          locations={[0, 0.45, 1]}
          start={{ x: 1, y: 0 }}
          end={{ x: 0, y: 0 }}
          style={[styles.podiumBlock, { height }]}
        >
          <Text style={styles.positionNumber}>{position}</Text>
        </LinearGradient>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.podiumRow}>
        {/* Silver - Position 2 */}
        <PodiumItem user={silver} position={2} />
        
        {/* Gold - Position 1 */}
        <PodiumItem user={gold} position={1} />
        
        {/* Bronze - Position 3 */}
        <PodiumItem user={bronze} position={3} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  podiumRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 8,
  },
  podiumItemContainer: {
    alignItems: 'center',
    flex: 1,
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: 8,
  },
  medalBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  userName: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: '#000',
    marginBottom: 4,
    textAlign: 'center',
  },
  userPoints: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: '#D89A6A',
    marginBottom: 8,
    textAlign: 'center',
  },
  podiumBlock: {
    width: '100%',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  positionNumber: {
    fontFamily: 'Inter_700Bold',
    fontSize: 48,
    color: '#FFF',
  },
});
